/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import IconButton from '@welldev-group/rcl-icon-button';
import { Avatar, I18nText } from '@welldev-group/react-comp-lib';
import { Menu } from '@welldev-group/rcl-menu';
import Popover from '@welldev-group/rcl-popover';
import { TableRow, TableData } from '@welldev-group/rcl-table';
import Icon from '@welldev-group/rcl-icon';
import routes from '../../../../../../shared/routes';
import EditTable from '../../../../User/EditTable';
import DateTime from '../../../../DateTime';

const UserTableRow = ({
  user,
  status,
  onUserReactivateClick,
  processVerification,
  onResendInvitation,
  onUserDeleteClick,
  signedInAsSuperAdmin,
  permissionOptions,
  permissionTemplateOptions,
  currentUserId,
  userAffiliateId,
  fetchUsers,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isPopoverVisible, setIsPopoverVisible] = useState(false);
  const onOutsideClick = () => {
    setIsVisible(false);
  };

  const ACTIVE_STATE = 'active';
  const DEACTIVATED_STATE = 'deactivated';
  const PENDING_STATE = 'pending';

  const hideMenu =
    status !== PENDING_STATE &&
    !user.reVerifiable &&
    user.superAdmin &&
    !user.verifiable;

  const mapUserTeams = (teams) =>
    teams.map((team) => ({
      value: team.id,
      label: team.name,
    }));

  return (
    <>
      <TableRow>
        <TableData>
          <div className="affiliate-user-list__name">
            <Avatar
              src={user.avatar}
              firstName={user.firstName}
              lastName={user.lastName}
              size="tiny"
            />
            <span>{user.name}</span>
          </div>
        </TableData>
        <TableData>
          <div className="affiliate-user-list__email">{user.email}</div>
        </TableData>
        <TableData>
          <div className="affiliate-user-list__mobile-number">
            {user.mobileNumber || (
              <I18nText id="affiliate_three_sixty.affiliate.briefing.not_available" />
            )}
          </div>
        </TableData>
        {status !== PENDING_STATE && (
          <TableData align="center">
            <span className="col-grid col-bleed align-center">
              <Icon
                name="checkCircle"
                color={user.reVerifiable ? 'success' : 'default'}
              />
            </span>
          </TableData>
        )}
        <TableData>
          <div className="affiliate-user-list__teams">
            {user.teams.length > 0 ? (
              <>
                {user.teams[0].name}
                {user.teams.length > 1 && (
                  <Popover
                    className="settings__sections-header-locale-wrapper"
                    size="small"
                    isVisible={isPopoverVisible}
                    direction="bottom-right"
                    onOutsideClick={() => setIsPopoverVisible(false)}
                    content={
                      <div className="col-grid col-bleed affiliate-user-list__teams-dropdown">
                        {user.teams.length > 1 &&
                          user.teams.slice(1).map((team) => (
                            <span
                              key={team.id}
                              className="affiliate-user-list__teams-dropdown-item"
                            >
                              {team.name}
                            </span>
                          ))}
                      </div>
                    }
                  >
                    <div className="affiliate-user-list__teams-link">
                      {user.teams.length > 1 && `+${user.teams.length - 1}`}
                    </div>
                  </Popover>
                )}
              </>
            ) : (
              <span className="affiliate-user-list__unassigned">
                <I18nText id="user.list.unassigned" />
              </span>
            )}
          </div>
        </TableData>
        {status === PENDING_STATE && (
          <TableData>
            {user.invitedAt ? (
              <DateTime dateTime={user.invitedAt} format="long" />
            ) : (
              <I18nText id="affiliate_three_sixty.affiliate.briefing.not_available" />
            )}
          </TableData>
        )}
        {status === ACTIVE_STATE && (
          <TableData>
            {user.lastActive ? (
              <DateTime dateTime={user.lastActive} format="long" />
            ) : (
              <I18nText id="affiliate_three_sixty.affiliate.briefing.not_available" />
            )}
          </TableData>
        )}
        {status === DEACTIVATED_STATE && (
          <TableData>
            {user.updatedAt ? (
              <DateTime dateTime={user.updatedAt} format="short" />
            ) : (
              <I18nText id="affiliate_three_sixty.affiliate.briefing.not_available" />
            )}
          </TableData>
        )}
        <TableData>
          {status === DEACTIVATED_STATE && (
            <div className="affiliate-user-list__menu">
              <IconButton
                version="v2"
                isIconOnly={true}
                icon={<Icon name="powerOff" />}
                size="large"
                onClick={() => onUserReactivateClick(user)}
                label={<I18nText id="user.actions.reactivate" />}
              />
            </div>
          )}
          {status !== DEACTIVATED_STATE && (
            <div className="affiliate-user-list__menu">
              <Menu
                className="affiliate-user-list__menu-dropdown"
                isVisible={isVisible}
                size="small"
                popOverDirection="bottom-right"
                onOutsideClick={onOutsideClick}
                label={
                  !hideMenu && (
                    <IconButton
                      size="small"
                      color="tertiary"
                      label={<I18nText id="show_more.more" />}
                      labelPlacement="bottom"
                      onClick={() => {
                        setIsVisible(
                          (previousIsVisibleState) => !previousIsVisibleState,
                        );
                      }}
                      icon={<Icon name="horizontalDots" />}
                    />
                  )
                }
              >
                <>
                  {status === PENDING_STATE && (
                    <span
                      className="affiliate-user-list__menu-item"
                      role="button"
                      onClick={() => onResendInvitation(user)}
                      onKeyDown={() => onResendInvitation(user)}
                      tabIndex={0}
                    >
                      <I18nText id="affiliate_three_sixty.affiliate.users.resend_invite.button" />
                    </span>
                  )}
                  {user.reVerifiable && status !== PENDING_STATE && (
                    <span
                      className="affiliate-user-list__menu-item"
                      role="button"
                      onClick={() => processVerification(user, true)}
                      onKeyDown={() => processVerification(user, true)}
                      tabIndex={0}
                    >
                      <I18nText id="affiliate_three_sixty.affiliate.users.reverify_email" />
                    </span>
                  )}
                  {!user.reVerifiable &&
                    user.verifiable &&
                    status !== PENDING_STATE && (
                      <span
                        className="affiliate-user-list__menu-item"
                        role="button"
                        onClick={() => processVerification(user)}
                        onKeyDown={() => processVerification(user)}
                        tabIndex={0}
                      >
                        <I18nText id="affiliate_three_sixty.affiliate.users.verify_email" />
                      </span>
                    )}
                  {!user.superAdmin && (
                    <span
                      className="affiliate-user-list__menu-item"
                      role="button"
                      tabIndex={0}
                      onClick={() => setModalOpen(true)}
                    >
                      <I18nText id="affiliate_three_sixty.affiliate.users.table.edit" />
                    </span>
                  )}
                  {user.id !== currentUserId && !user.superAdmin && (
                    <span
                      className="affiliate-user-list__menu-item"
                      role="button"
                      onClick={() => onUserDeleteClick(user)}
                      onKeyDown={() => onUserDeleteClick(user)}
                      tabIndex={0}
                    >
                      {status === PENDING_STATE ? (
                        <I18nText id="affiliate_three_sixty.affiliate.users.table.delete" />
                      ) : (
                        <I18nText id="affiliate_three_sixty.affiliate.users.table.deactivate" />
                      )}
                    </span>
                  )}
                </>
              </Menu>
            </div>
          )}
        </TableData>
      </TableRow>
      <EditTable
        currentUserId={currentUserId}
        affiliateId={userAffiliateId}
        userInitialAttributes={user}
        previousSelectedTeam={mapUserTeams(user.teams)}
        permissionOptions={permissionOptions}
        permissionTemplateOptions={permissionTemplateOptions}
        fetchUsers={fetchUsers}
        submitURL={routes.users.update}
        buttonClass="user-index__edit"
        signedInAsSuperAdmin={signedInAsSuperAdmin}
        onModalClose={() => setModalOpen(false)}
        isVisible={modalOpen}
      />
    </>
  );
};

UserTableRow.defaultProps = {
  permissionTemplateOptions: [],
};

UserTableRow.propTypes = {
  status: PropTypes.string.isRequired,
  fetchUsers: PropTypes.func.isRequired,
  onUserReactivateClick: PropTypes.func.isRequired,
  processVerification: PropTypes.func.isRequired,
  onResendInvitation: PropTypes.func.isRequired,
  onUserDeleteClick: PropTypes.func.isRequired,
  user: PropTypes.shape({
    name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    verified: PropTypes.bool,
    teams: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
      }).isRequired,
    ),
    lastActive: PropTypes.string,
  }).isRequired,
  signedInAsSuperAdmin: PropTypes.bool.isRequired,
  userAffiliateId: PropTypes.number.isRequired,
  permissionOptions: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string))
    .isRequired,
  permissionTemplateOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      permissions: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
  ),
  currentUserId: PropTypes.number.isRequired,
};

export default UserTableRow;
